import { CadencePeriod, ChartFilterId, ChartType, RawChartSchema } from 'common/types/chart';
import { Brand, Country, Wave } from 'common/types/common';
import {
  AllFiltersData,
  Cadences,
  FilterNames,
  FilterOptionType,
  FiltersOptions,
  ChartFilter,
  IFilterOption,
  IFilterOptionGroup,
  RestrictionConfig,
  RestrictionsPayload,
} from 'common/types/filters';
import { getColorForData } from 'modules/charts/utils/colors';
import { normalizeWave } from 'utils/wave';
import { arrayEquals, deepValue, uniqueListItemsByIdentifier } from 'utils/helpers';
import { ChartFormData } from 'common/types/chart';
import { FilterLabels, Identifier, NO_BRAND } from 'settings/constants';
import { SegmentGroups } from 'common/types/segments';
import { displayDate } from './dates';
import { KPIGroups } from 'common/types/kpi';
import { FilterValuesByType } from 'modules/charts/types';
import { groupWavesToOptions } from './filters/groupWavesToOptions';
import { mapCustomKpiGroup, mapKpiOption } from './filters/mapKpiOption';

const mapBrandOrCountryOption = ({ id, name }: Brand | Country): IFilterOption => ({
  value: id,
  label: name,
  type: FilterOptionType.OPTION,
});

const mapBrand = (brand: Brand): Brand => ({
  ...brand,
  name: !brand.global ? brand.name : NO_BRAND,
});

export const normalizeRestrictionsPayload = (data: RestrictionsPayload): RestrictionsPayload => ({
  ...data,
  brands: (data?.brands || []).map(mapBrand),
  waves: (data?.waves || []).map(normalizeWave),
  countries: (data?.countries || []).map((country, i) => ({ ...country, color: getColorForData(i) })),
});

const groupBrandsToAvailableAndPartiallyAvailable = (brands: Brand[] | undefined): (IFilterOption | IFilterOptionGroup)[] => {
  if (!brands || brands.length === 0) {
    // Return a single group if brands are undefined or empty
    return [
      {
        label: 'No Brands Available',
        options: [],
        type: FilterOptionType.GROUP,
      },
    ];
  }

  // Separate brands into two groups based on available_for_all_waves
  const availableBrands = brands.filter(brand => brand.wave_availability?.available_for_all_waves);
  const partiallyAvailableBrands = brands.filter(brand => !brand.wave_availability?.available_for_all_waves);

  // Create groups based on availability
  const groups: IFilterOptionGroup[] = [];

  if (availableBrands.length > 0) {
    groups.push({
      label: 'Available brands',
      options: availableBrands.map(mapBrandOrCountryOption),
      type: FilterOptionType.GROUP,
    });
  }

  if (partiallyAvailableBrands.length > 0) {
    groups.push({
      label: 'Partially available',
      options: partiallyAvailableBrands.map(mapBrandOrCountryOption),
      type: FilterOptionType.GROUP,
      infoMessage: 'The below brands have data available for only a portion of the selected filter configuration',
    });
  }

  // If both groups are empty, return a single group indicating no brands are available
  if (groups.length === 0) {
    return [
      {
        label: 'No Brands Available',
        options: [],
        type: FilterOptionType.GROUP,
      },
    ];
  }

  return groups;
};

const generateWavesOrder = (waves: Wave[] | undefined) => {
  return waves?.sort((waveA, waveB) => (new Date(waveA.date) > new Date(waveB.date) ? -1 : 1)).map(({ id }) => id);
};

const mapSegmentGroupOptions = ({ name: label, segments }: SegmentGroups): IFilterOptionGroup => ({
  label,
  type: FilterOptionType.GROUP,
  options: segments.map(({ id: value, name: label }) => ({
    label,
    value,
    type: FilterOptionType.OPTION,
  })),
});

export const generateFiltersData = (options: FiltersOptions, chartType: ChartType): ChartFilter[] => {
  const { availableFilters, brands, segmentGroups, kpis, selectedKPIFormulas, cadenceEntry, customKpis = [] } = options;

  const mappedTimePeriods = cadenceEntry?.map(normalizeWave);
  const includeGlobal = kpis.some(kpi => kpi.global === true);
  const brandsList = !includeGlobal ? brands.filter(brand => brand.global !== true) : brands;

  const filters = [
    {
      label: FilterLabels.Geography,
      id: FilterNames.COUNTRY,
      options: [
        {
          label: 'Countries',
          options: availableFilters?.countries.map(mapBrandOrCountryOption),
          type: FilterOptionType.GROUP,
        },
      ] as IFilterOptionGroup[],
    },
    {
      label: FilterLabels.TimePeriod,
      id: FilterNames.WAVE,
      optionsOrder: generateWavesOrder(mappedTimePeriods),
      options: groupWavesToOptions(mappedTimePeriods),
    },
    {
      label: FilterLabels.KPI,
      id: FilterNames.KPI,
      options: kpis.reduce(mapKpiOption(chartType, selectedKPIFormulas), []).concat(customKpis.length ? mapCustomKpiGroup(customKpis) : []),
    },
    {
      label: FilterLabels.Brand,
      id: FilterNames.BRAND,
      options: groupBrandsToAvailableAndPartiallyAvailable(brandsList),
    },
    {
      label: FilterLabels.Segment,
      id: FilterNames.SEGMENT,
      options: segmentGroups.map(mapSegmentGroupOptions),
    },
  ];

  return filters;
};

export const getIntermediateChangedMap = (
  intermediateChangedMap: Record<string, boolean>,
  filterId: string,
  values: unknown,
  intermediateValues: ChartFormData,
): Record<string, boolean> => {
  const prevValues = deepValue(intermediateValues, filterId);
  const prevDiffs = intermediateChangedMap[filterId];
  const differs = Array.isArray(values) ? !arrayEquals(prevValues as Array<string | number>, values) : prevValues !== values;
  return prevDiffs !== differs ? { ...intermediateChangedMap, [filterId]: differs } : intermediateChangedMap;
};

export const getFilteredWaves = (
  chartDataWave: (string | number)[],
  filtersOptionsWave: (string | number)[] | undefined,
): Array<string | number> => {
  return chartDataWave.filter(value => filtersOptionsWave?.includes(value));
};

export const composeRestrictions = (previousValue: RestrictionsPayload, currentValue: RestrictionsPayload): RestrictionsPayload => {
  const { cadences: prevCadences, ...prevFilters } = previousValue;
  const { cadences: currCadences, ...currFilters } = currentValue;

  const filters = {} as Record<string, (Brand | Wave | Country)[]>;
  Object.keys(currFilters).forEach(key => {
    filters[key] = uniqueListItemsByIdentifier(
      [...prevFilters[key as keyof typeof prevFilters], ...currFilters[key as keyof typeof currFilters]],
      Identifier.ID,
    );
  });

  const cadences = Object.values(CadencePeriod).reduce((acc, period) => {
    acc[period] = uniqueListItemsByIdentifier([...(prevCadences?.[period] ?? []), ...(currCadences?.[period] ?? [])], Identifier.ID);
    return acc;
  }, {} as Cadences);

  return { ...(filters as Omit<RestrictionsPayload, 'cadences'>), cadences };
};

export const isAllSelected = (allOptions: (string | number)[] | undefined, selectedOptions: (string | number)[] | undefined): boolean => {
  if (!allOptions?.length || !selectedOptions?.length) return false;
  return allOptions?.every((option: string | number) => selectedOptions?.indexOf(option) !== -1);
};

export const getFiltersConfig = (payload: Record<string, number[]>): Array<RestrictionConfig> => {
  const nonEmptyFilters = Object.entries(payload).filter(([, values]) => values && values.length);
  return nonEmptyFilters.reduce((acc: Array<RestrictionConfig>, curr: [string, number[]]) => {
    const [name, values] = curr;
    acc = [...acc, { name, values }];
    return acc;
  }, []);
};

export const getSortedValues = (optionsOrder: (string | number)[] | undefined, values: (string | number)[]): Array<string | number> => {
  if (!optionsOrder) return values;
  return values.sort((valA, valB) => {
    const indexA = optionsOrder.indexOf(valA);
    const indexB = optionsOrder.indexOf(valB);
    return indexB - indexA;
  });
};

const filterSelectedKPI = (selectedOptions: Array<ChartFilterId>, value: ChartFilterId, kpiGroups: KPIGroups) => {
  let kpisToDeselect: Array<ChartFilterId> = [];
  kpiGroups.absolute.includes(value as string) ? (kpisToDeselect = kpiGroups.percentage) : (kpisToDeselect = kpiGroups.absolute);
  return selectedOptions.filter((item: ChartFilterId) => !kpisToDeselect.includes(item));
};

export const handleOptionSelection = (
  chartValues: Array<ChartFilterId>,
  newValue: ChartFilterId,
  isKPIFilter: boolean,
  kpiTypesGroups: KPIGroups,
): Array<ChartFilterId> => {
  let selectedOptions: Array<ChartFilterId> = [];
  const selected = chartValues.includes(newValue);

  selectedOptions = selected ? chartValues.filter((value: ChartFilterId) => newValue !== value) : [...chartValues, newValue];

  if (isKPIFilter && !selected) {
    selectedOptions = filterSelectedKPI(selectedOptions, newValue, kpiTypesGroups);
  }

  return selectedOptions;
};

export const getChartBrandAndSegmentNames = (
  chartFormData: ChartFormData,
  filtersOptions: FiltersOptions,
): { brandNames: string[]; segmentNames: string[] } => {
  const brandNames: string[] = [];
  const segmentNames: string[] = [];

  // Get brand names
  (Array.isArray(chartFormData.brand) ? chartFormData.brand : []).forEach(brandId => {
    const brand = filtersOptions.brands.find(b => b.id === brandId);
    if (brand) {
      brandNames.push(brand.name);
    }
  });

  // Get segment names
  (Array.isArray(chartFormData.segment) ? chartFormData.segment : []).forEach(segmentId => {
    for (const segmentGroup of filtersOptions.segmentGroups) {
      const segment = segmentGroup.segments.find(s => s.id === segmentId);
      if (segment) {
        segmentNames.push(segment.name);
        break; // Found the segment, no need to search further
      }
    }
  });

  return { brandNames, segmentNames };
};

export const getFilterLabelById = <T extends { id: string | number; name: string }>(
  items: T[] | undefined,
  id: string | number,
): string => {
  return items?.find(item => item.id === id)?.name || '';
};

export const getNamesForAllFilterIds = (chartSchema: RawChartSchema, chartFilters: AllFiltersData): FilterValuesByType => {
  const { country_ids, wave_ids, kpi_option_identifiers, brand_ids, segment_ids } = chartSchema;

  const findName = (ids: number[], items: { id: number; name: string }[] | undefined) =>
    ids.map(id => items?.find(item => item.id === id)?.name).filter((name): name is string => !!name);

  const findDate = (ids: number[], waves: Wave[] | undefined) =>
    ids.map(id => waves?.find(wave => wave.id === id)?.date).filter((date): date is Date => !!date);

  return {
    countries: findName(country_ids, chartFilters.countries),
    waves: findDate(wave_ids, chartFilters.waves).map(date => displayDate(date, 'MMMM YYYY')),
    kpis: kpi_option_identifiers.map(id => chartFilters.kpis[id] || ''),
    brands: findName(brand_ids, chartFilters.brands),
    segments: findName(segment_ids, chartFilters.segments),
  };
};
